import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Star } from "lucide-react";
import type { ChartConfig } from "@/components/ui/chart";
import { AnalyticsDonutChartArea } from "../shared/AnalyticsDonutChartArea";
import { RevenueChartCard } from "../revenue/RevenueChartCard";
import { SERVICES_CHART_CARD_ICON_COLOR } from "../services/constants";
import {
  REVIEWS_CATEGORY_CARD_HEIGHT,
  REVIEWS_COLORS,
  REVIEWS_CATEGORY_LABELS,
} from "./constants";
import type { CategoryKey } from "./types";

interface ReviewsAverageRatingCardProps {
  overallRating: number | null;
  categoryKeys: CategoryKey[];
  categoryAverages: Partial<Record<CategoryKey, number>>;
}

const CATEGORY_COLORS: Record<CategoryKey, string> = {
  purity: REVIEWS_COLORS.purity,
  comfort: REVIEWS_COLORS.comfort,
  service: REVIEWS_COLORS.service,
};

export const ReviewsAverageRatingCard: React.FC<
  ReviewsAverageRatingCardProps
> = ({ overallRating, categoryKeys, categoryAverages }) => {
  const { t } = useTranslation();

  const segments = useMemo(
    () =>
      categoryKeys.map((key) => ({
        key,
        name: t(
          `analytics.reviews.categories.${key}`,
          REVIEWS_CATEGORY_LABELS[key],
        ),
        value: categoryAverages[key] ?? 0,
        fill: CATEGORY_COLORS[key],
      })),
    [categoryAverages, categoryKeys, t],
  );

  const chartConfig = useMemo(
    () =>
      segments.reduce<ChartConfig>((config, segment) => {
        config[segment.key] = {
          label: segment.name,
          color: segment.fill,
        };
        return config;
      }, {}),
    [segments],
  );

  const hasChartData = useMemo(
    () => segments.some((segment) => segment.value > 0),
    [segments],
  );

  const overallLabel = t("analytics.reviews.overall", "Overall");

  return (
    <RevenueChartCard
      title={t("analytics.reviews.averageRating", "Average Rating")}
      icon={<Star />}
      iconColor={SERVICES_CHART_CARD_ICON_COLOR}
      width="full"
      showExport={false}
      className="box-border min-w-0 flex-1"
      style={{
        height: REVIEWS_CATEGORY_CARD_HEIGHT,
        minHeight: REVIEWS_CATEGORY_CARD_HEIGHT,
      }}
    >
      <div className="flex min-h-0 flex-1 flex-col items-center justify-between gap-4">
        <AnalyticsDonutChartArea
          segments={segments}
          total={0}
          hasData={hasChartData}
          chartConfig={chartConfig}
          centerPrimary={overallRating != null ? overallRating.toFixed(1) : "—"}
          centerSecondary={overallLabel}
        />

        <ul className="flex w-full flex-col gap-2">
          {categoryKeys.map((key) => (
            <li
              key={key}
              className="flex items-center justify-between gap-2 text-sm text-[#171717]"
            >
              <div className="flex items-center gap-2">
                <span
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: CATEGORY_COLORS[key] }}
                />
                <span>
                  {t(
                    `analytics.reviews.categories.${key}`,
                    REVIEWS_CATEGORY_LABELS[key],
                  )}
                </span>
              </div>
              <span className="font-medium">
                {(categoryAverages[key] ?? 0).toFixed(1)}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </RevenueChartCard>
  );
};
